// @ts-nocheck
import { useMutation, useQueryClient } from 'react-query';
import { transformPagination, transformResponse } from '@/utils';
import { useRequestQuery } from '../useQueryRequest';
import useApiRequest from '../useRequest';
import t from './types';

const DEFAULT_PAGINATION = {
  pageSize: 20,
  page: 0,
  pagesCount: 0,
};

// Common invalidate queries.
const commonInvalidateQueries = (queryClient) => {
  // Invalidate items.
  queryClient.invalidateQueries(t.ITEMS);

  // Invalidate items categories.
  queryClient.invalidateQueries(t.ITEMS_CATEGORIES);
};

/**
 * Creates a new item.
 */
export function useCreateItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('items', values), {
    onSuccess: (res, values) => {
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edits the given item.
 */
export function useEditItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(([id, values]) => apiRequest.post(`items/${id}`, values), {
    onSuccess: (res, [id, values]) => {
      // Invalidate specific item.
      queryClient.invalidateQueries([t.ITEM, id]);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Deletes the given item.
 */
export function useDeleteItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`items/${id}`), {
    onSuccess: (res, id) => {
      // Invalidate specific item.
      queryClient.invalidateQueries([t.ITEM, id]);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Activate the given item.
 */
export function useActivateItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.post(`items/${id}/activate`), {
    onSuccess: (res, id) => {
      // Invalidate specific item.
      queryClient.invalidateQueries([t.ITEM, id]);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Inactivate the given item.
 */
export function useInactivateItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.post(`items/${id}/inactivate`), {
    onSuccess: (res, id) => {
      // Invalidate specific item.
      queryClient.invalidateQueries([t.ITEM, id]);

      // Common invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

// Transformes items response.
const transformItemsResponse = (response) => {
  return {
    items: response.data.items,
    pagination: transformPagination(
      transformResponse(response.data.pagination),
    ),
    filterMeta: transformResponse(response.data.filter_meta),
  };
};

/**
 * Retrieves items list.
 */
export function useItems(query, props) {
  return useRequestQuery(
    [t.ITEMS, query],
    {
      method: 'get',
      url: 'items',
      params: { ...query },
    },
    {
      select: transformItemsResponse,
      defaultData: {
        items: [],
        pagination: DEFAULT_PAGINATION,
        filterMeta: {},
      },
      ...props,
    },
  );
}

export function useRefreshItems() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.ITEMS);
    },
  };
}

/**
 * Retrieve details of the given item.
 * @param {number} id - Item id.
 */
export function useItem(id, props) {
  return useRequestQuery(
    [t.ITEM, id],
    {
      method: 'get',
      url: `items/${id}`,
    },
    {
      select: (response) => response.data.item,
      defaultData: {},
      ...props,
    },
  );
}

export function useItemCodeExists(query, props) {
  const exceptItemId = Number(query?.except_item_id);
  const safeQuery = {
    ...query,
    ...(Number.isFinite(exceptItemId)
      ? { except_item_id: exceptItemId }
      : {}),
  };

  return useRequestQuery(
    [t.ITEM_CODE_EXISTS, safeQuery],
    {
      method: 'get',
      url: 'items/code-exists',
      params: safeQuery,
    },
    {
      select: (response) => response.data,
      defaultData: { exists: false },
      ...props,
    },
  );
}

export function useItemAssociatedInvoiceTransactions(id, props) {
  return useRequestQuery(
    [t.ITEM_ASSOCIATED_WITH_INVOICES, id],
    {
      method: 'get',
      url: `items/${id}/transactions/invoices`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}

export function useItemAssociatedEstimateTransactions(id, props) {
  return useRequestQuery(
    [t.ITEM_ASSOCIATED_WITH_ESTIMATES, id],
    {
      method: 'get',
      url: `items/${id}/transactions/estimates`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}

export function useItemAssociatedReceiptTransactions(id, props) {
  return useRequestQuery(
    [t.ITEM_ASSOCIATED_WITH_RECEIPTS, id],
    {
      method: 'get',
      url: `items/${id}/transactions/receipts`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}
export function useItemAssociatedBillTransactions(id, props) {
  return useRequestQuery(
    [t.ITEMS_ASSOCIATED_WITH_BILLS, id],
    {
      method: 'get',
      url: `items/${id}/transactions/bills`,
    },
    {
      select: (res) => res.data.data,
      defaultData: [],
      ...props,
    },
  );
}

export function useItemWarehouseLocation(id, props) {
  return useRequestQuery(
    [t.ITEM_WAREHOUSES_LOCATION, id],
    {
      method: 'get',
      url: `items/${id}/warehouses`,
    },
    {
      select: (res) => res.data.item_warehouses,
      defaultData: [],
      ...props,
    },
  );
}

/**
 * 
 * @param {*} id 
 * @param {*} query 
 * @param {*} props 
 * @returns 
 */
export function useItemInventoryCost(query, props) {
  return useRequestQuery(
    [t.ITEM_INVENTORY_COST, query],
    {
      method: 'get',
      url: `inventory/items-cost`,
      params: { ...query },
    },
    {
      select: (res) => res.data.costs,
      defaultData: [],
      ...props,
    },
  ); 
}

/**
 * Looks up an item SKU by its SKU code and returns the SKU id.
 * @param {string} itemSku - The SKU code to look up.
 */
export function useItemSkuLookup(itemSku, props) {
  return useRequestQuery(
    [t.ITEM_SKU_LOOKUP, itemSku],
    {
      method: 'get',
      url: 'items/skus/lookup',
      params: { item_sku: itemSku },
    },
    {
      select: (res) => res.data,
      defaultData: null,
      enabled: !!itemSku,
      ...props,
    },
  );
}

export function useItemSkus(itemId, props) {
  return useRequestQuery(
    [t.ITEM_SKUS, itemId],
    {
      method: 'get',
      url: `items/${itemId}/skus`,
    },
    {
      select: (res) => res.data.item_skus ?? [],
      defaultData: [],
      enabled: !!itemId,
      ...props,
    },
  );
}

export function useItemInventoryHistory(itemId, skuId, props) {
  return useRequestQuery(
    [t.ITEM_INVENTORY_HISTORY, itemId, skuId],
    {
      method: 'get',
      url: `items/${itemId}/skus/${skuId}/inventory-history`,
    },
    {
      select: (res) => res.data.data ?? [],
      defaultData: [],
      enabled: !!itemId && !!skuId,
      ...props,
    },
  );
}

export function useItemInventoryRecords(itemId, skuId, props) {
  return useRequestQuery(
    [t.ITEM_INVENTORY_RECORDS, itemId, skuId],
    {
      method: 'get',
      url: `items/${itemId}/skus/${skuId}/inventory-records`,
    },
    {
      select: (res) => res.data.data ?? [],
      defaultData: [],
      enabled: !!itemId && !!skuId,
      ...props,
    },
  );
}

export function useItemInventoryOrders(itemId, skuId, props) {
  return useRequestQuery(
    [t.ITEM_INVENTORY_ORDERS, itemId, skuId],
    {
      method: 'get',
      url: `items/${itemId}/skus/${skuId}/inventory-orders`,
    },
    {
      select: (res) => res.data.data ?? [],
      defaultData: [],
      enabled: !!itemId && !!skuId,
      ...props,
    },
  );
}
